'use client';

import { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Spin } from '@/components/ui-feat/spin';
import { isEmpty } from 'lodash-es';
import { claimReward, useGetRewardPopupList } from '@/features/reward/services/rewardcenter';
import { rewardCardDrawerHelper } from '@/features/reward/components/reward-card-drawer/helpers/rewardCardDrawerHelper';
import { RewardStatusEnum, RewardTypeEnum } from '@/types/reward';
import { CashbackReward } from '@/features/reward/components/reward-card-drawer/base/cashback-reward';
import { CumulativeReward } from '@/features/reward/components/reward-card-drawer/base/cumulative-reward';
import PeriodicReward from '@/features/reward/components/reward-card-drawer/base/periodic-reward';
import { RankingReward } from '@/features/reward/components/reward-card-drawer/base/ranking-reward';
import { ReferralReward } from '@/features/reward/components/reward-card-drawer/base/refferral-reward';
import TurntableReward from '@/features/reward/components/reward-card-drawer/base/turntable-reward';
import { UnlockedReward } from '@/features/reward/components/reward-card-drawer/base/unlocked-reward';
import { VipUpgradeReward } from '@/features/reward/components/reward-card-drawer/base/vip-upgrade-reward';
import { CalenderReward } from '@/features/reward/components/reward-card-drawer/base/calender-reward';
import { WelcomeReward } from '@/features/reward/components/reward-card-drawer/base/welcome-reward';
import { showClaimDrawer } from '@/features/reward/components/claim-drawer';
import { useRouter } from '@/i18n/navigation';
import { useRewardCountManager } from '@/features/reward/store/reward-count';
import { useRewardCenterManager } from '@/features/reward/store/reward-center-list';
import Empty from '@/components/ui-feat/empty';
import { track } from '@/utils/track';
import { useCurrencyRate } from '@/features/ranking/hooks/useCurrencyRate';
import { RewardTypeTextMap } from '@/features/reward/const/event';
import { useTranslations } from 'next-intl';
import { AudioId, useAudioHelper } from '@/utils/audio-hepler';
import toast from 'react-hot-toast';
import { RewardTypeHelper } from '@/features/reward/helpers/reward-type';
import { LOGIN_SOURCE_MAP } from '@/const/report';
import type { RewardDrawerCardItem } from '@/features/reward/components/reward-card-drawer/types';

function getProgress(progressList: RewardDrawerCardItem['progressList'], type: 'Wagered' | 'Deposit' | 'Cash') {
  return (progressList || []).find((progress) => progress.type === type);
}

interface BonusTabProps {
  /** drawer 是否可见，用于触发 refetch */
  isVisible: boolean;
  /** 关闭 drawer 的回调 */
  onHide: () => void;
}

export default function BonusTab({ isVisible, onHide }: BonusTabProps) {
  const { playAudio } = useAudioHelper({ id: AudioId.RewardReceived });
  const { data, isLoading, refetch } = useGetRewardPopupList({
    queryConfig: { enabled: false },
  });
  const router = useRouter();
  const rewardCardList = isLoading || isEmpty(data) ? [] : rewardCardDrawerHelper.sortRewardCardData(data.list);
  const { updateClaimCount } = useRewardCountManager();
  const { updateCenterList } = useRewardCenterManager();
  const { getRate } = useCurrencyRate();
  const t = useTranslations('reward');
  const t_common = useTranslations('common');

  useEffect(() => {
    if (isVisible) {
      refetch();
      // 产品希望晚一点比click
      setTimeout(() => {
        track('reward_page_view', {
          page_type: LOGIN_SOURCE_MAP.RewardsPopup,
        });
      }, 100);
    }
  }, [isVisible]);

  const onClaim = async (item) => {
    if ([
      RewardTypeEnum.WELCOME_GIFT,
      RewardTypeEnum.CUMULATIVE_REWARD,
      RewardTypeEnum.TURNTABLE_REWARD,
      RewardTypeEnum.CASH_BACK,
      RewardTypeEnum.WEEKLY_REWARD,
      RewardTypeEnum.MONTHLY_REWARD,
    ].includes(item.type as RewardTypeEnum)) {
      track('reward_page_button_click', {
        page_type: 'Rewards Popup',
        reward_type: RewardTypeTextMap[item.type],
        button_name: 'claim',
      });
    }
    try {
      const res = await claimReward({ params: { rewardCard: item, reward_type: 1 } });
      if (item.type === RewardTypeEnum.RANKING_REWARD) {
        const currency = item.currency;
        const exchange = getRate({ currency, amount: item.amount });
        track('leaderboard_prize_claim', {
          term: item.term,
          leaderboard_time: item.date_timestamp,
          claim_method: 'Manual Claim',
          exchange_rate: Number(exchange?.rate),
          reward_currency: item.currency,
          reward_amount: Number(item.amount),
          reward_usd_value: exchange?.exchange_amount,
        });
      }
      refetch();
      updateClaimCount();
      updateCenterList();
      playAudio();
      showClaimDrawer({
        amount: item.amount,
        currency: item.currency,
        claimAmount: item?.claimAmount,
        claimCurrency: item?.claimCurrency,
        rewards: RewardTypeHelper.isVipReward(item) ? item.rewards : [{
          money: { amount: item.claimAmount, currency: item.claimCurrency },
          wallet: { type: item.claimType, water_multiple: item.bonusWagerMultiple },
          type: item.type,
        }],
      });
      return res;
    } catch (error) {
      toast.error((error as any)?.response?.data?.msg || t_common('errors.networkError'));
    }
  };

  const onSpin = (item) => {
    if ([
      RewardTypeEnum.WELCOME_GIFT,
      RewardTypeEnum.CUMULATIVE_REWARD,
      RewardTypeEnum.TURNTABLE_REWARD,
      RewardTypeEnum.CASH_BACK,
      RewardTypeEnum.WEEKLY_REWARD,
      RewardTypeEnum.MONTHLY_REWARD,
    ].includes(item.type as RewardTypeEnum)) {
      track('reward_page_button_click', {
        page_type: 'Rewards Popup',
        reward_type: RewardTypeTextMap[item.type],
        button_name: 'spin',
      });
    }
    onHide();
  };

  const onTurntable = async (item) => {
    const actionMap = {
      [RewardStatusEnum.INVITE_NOT_PARTICIPATE]: 'get cash',
      [RewardStatusEnum.INVITE]: 'invite',
      [RewardStatusEnum.SPIN]: 'spin',
      [RewardStatusEnum.CLAIM]: 'claim',
    };
    await track('reward_page_button_click', {
      page_type: 'Rewards Popup',
      reward_type: RewardTypeTextMap[item.type],
      button_name: actionMap[item.status],
    });
    onHide();
  };

  const onDeposit = (item) => {
    if ([
      RewardTypeEnum.WELCOME_GIFT,
      RewardTypeEnum.CUMULATIVE_REWARD,
      RewardTypeEnum.TURNTABLE_REWARD,
      RewardTypeEnum.CASH_BACK,
      RewardTypeEnum.WEEKLY_REWARD,
      RewardTypeEnum.MONTHLY_REWARD,
    ].includes(item.type as RewardTypeEnum)) {
      track('reward_page_button_click', {
        page_type: 'Rewards Popup',
        reward_type: RewardTypeTextMap[item.type],
        button_name: 'deposit',
      });
    }
    onHide();
  };

  const handleJumpToReward = () => {
    onHide();
    router.push('/reward');
  };

  const renderRewardCardList = () => {
    if (isEmpty(rewardCardList)) {
      return <Empty className='h-full' />;
    }
    return rewardCardList.map((item, i) => {
      switch (item.type) {
        case RewardTypeEnum.CALENDAR_REWARD:
          return <CalenderReward key={i} {...item as any} amount={+(item.amount || 0)} onClaim={() => onClaim(item)} onTimeOut={() => refetch()} />;
        case RewardTypeEnum.PROMOTION_REWARD:
          return <VipUpgradeReward key={i} {...item as any} amount={+(item.amount || 0)} onClaim={() => onClaim(item)} />;
        case RewardTypeEnum.CASH_BACK:
          return <CashbackReward key={i} {...item as any} amount={+(item.amount || 0)} onClaim={() => onClaim(item)} onTimeOut={() => refetch()} />;
        case RewardTypeEnum.WEEKLY_REWARD:
          return <PeriodicReward key={i} {...item as any} amount={+(item.amount || 0)} wageredProgress={getProgress(item.progressList, 'Wagered')} onClaim={() => onClaim(item)} onTimeOut={() => refetch()} />;
        case RewardTypeEnum.MONTHLY_REWARD:
          return <PeriodicReward key={i} {...item as any} amount={+(item.amount || 0)} wageredProgress={getProgress(item.progressList, 'Wagered')} onClaim={() => onClaim(item)} onTimeOut={() => refetch()} />;
        case RewardTypeEnum.DAILY_REWARD:
          return <PeriodicReward key={i} {...item as any} amount={+(item.amount || 0)} wageredProgress={getProgress(item.progressList, 'Wagered')} onClaim={() => onClaim(item)} onTimeOut={() => refetch()} />;
        case RewardTypeEnum.CUMULATIVE_REWARD:
          return <CumulativeReward key={i} {...item as any} amount={+(item.amount || 0)} depositProgress={getProgress(item.progressList, 'Deposit')} onSpin={() => onSpin(item)} />;
        case RewardTypeEnum.REWARD_UNLOCK:
          return <UnlockedReward key={i} {...item as any} amount={+(item.amount || 0)} wageredProgress={getProgress(item.progressList, 'Wagered')} {...(item.rewardUnlock || {})} onClaim={() => onClaim(item)} />;
        case RewardTypeEnum.TURNTABLE_REWARD:
          return <TurntableReward key={i} {...item as any} amount={+(item.amount || 0)} cashProgress={getProgress(item.progressList, 'Cash')} onTurntable={() => onTurntable(item)} />;
        case RewardTypeEnum.INVITE_REWARD:
          return <ReferralReward key={i} {...item as any} amount={+(item.amount || 0)} wageredProgress={getProgress(item.progressList, 'Wagered')} depositProgress={getProgress(item.progressList, 'Deposit')} onClaim={() => onClaim(item)} />;
        case RewardTypeEnum.RANKING_REWARD:
          return <RankingReward key={i} {...item as any} amount={+(item.amount || 0)} onClaim={() => onClaim(item)} />;
        case RewardTypeEnum.WELCOME_GIFT:
          return <WelcomeReward key={i} {...item as any} bonusPercentage={(item?.fluctuationCountdown || 0) > 0 ? item.bonusPercentage : item.giftPercentage} amount={+(item.amount || 0)} id={item?.id || 0} onDeposit={() => onDeposit(item)} />;
        default:
          return null;
      }
    });
  };

  return (
    <div className='flex-1 overflow-hidden overflow-y-auto no-scrollbar flex flex-col'>
      <div className='flex-1 overflow-hidden overflow-y-auto no-scrollbar space-y-2.5'>
        {isLoading && <Spin className='h-full' />}
        {!isLoading && renderRewardCardList()}
      </div>
      <Button className='w-full my-7' variant='primary' onClick={handleJumpToReward}>{t('viewAll')}</Button>
    </div>
  );
}
